import { Trans, useTranslation } from 'react-i18next';

import { ConfirmBody, EmailVerifyBody } from '@repo/ui/overlay';
import { useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';

import { MY_PROFILE_KEY, useGetMyProfile } from 'services/hooks/user';
import http from 'services/http';
import { useModalStackStore } from 'store/modalStackStore';
import { handleControlAuthError } from 'utils/error';
import { LOADING_KEY } from 'utils/promise';
import { handleFailToast } from 'utils/toast/handleToast';

import ModificationButton from '../shared/ModificationButton';
import VerificationButton, { BaseVerificationProps } from '../shared/VerificationButton';

import type { FormikProps } from 'formik';

export const EmailVerificationButton = ({ isVerificationCompleted }: BaseVerificationProps) => {
  const queryClient = useQueryClient();
  const { data: profileData } = useGetMyProfile();
  const { openModal, closeModal, closeAllModals } = useModalStackStore();

  const email = profileData?.email || '';

  const handleRequestEmailVerification = async (email: string) => {
    await http.get(`/my/verify`, { email }, { useErrorToast: false, useLoading: false });

    openModal(
      <ConfirmBody
        transComponents={<Trans i18nKey={'setting.edit_email_verify_success_message'} />}
        title={t('setting.edit_email_verify_success_message')}
        contents={t('setting.edit_email_verify_success_info_text')}
        onClickButton={() => queryClient.invalidateQueries({ queryKey: MY_PROFILE_KEY })}
        closeAllModals={closeAllModals}
      />,
      {
        modalWrapperStyle: { maxWidth: 320 },
        closeOnBackdropClick: false,
      }
    );
  };

  const handleEmailVerificationError = (error: unknown, formik?: FormikProps<{ email: string | undefined }>) => {
    if (typeof error === 'object' && error !== null && 'code' in error && 'statusCode' in error) {
      if (error.statusCode === 400 && error.code === 'UNKNOWN_ERROR_HTTP') {
        handleFailToast(t('setting.edit_email_verify_validation_text'));
        return;
      }

      if (error.statusCode === 400 && error.code === 'EMAIL_ALREADY_VERIFIED') {
        formik?.setFieldError('email', t('setting.edit_email_verify_email_template_expiration_message'));
        return;
      }

      if (error.statusCode === 500 && error.code === 'UNKNOWN_ERROR') {
        handleFailToast(t('identity.verification_failed_reason_unknown'));
        return;
      }
    }
    handleControlAuthError(error, formik);
  };

  const handleOpenEmailVerificationModal = () => {
    openModal(
      <EmailVerifyBody
        email={email}
        isVerified={false}
        onClickSubmit={handleRequestEmailVerification}
        onControlError={handleEmailVerificationError}
        closeModal={closeModal}
      />,
      {
        closeOnBackdropClick: false,
      }
    );
  };

  return (
    <VerificationButton
      isDisabled={false}
      isCompleted={isVerificationCompleted}
      onClick={handleOpenEmailVerificationModal}
      completedText="chat.rewards_creator_information_edit"
      loadingKey={LOADING_KEY.PROFILES}
    />
  );
};

export const EmailModificationButton = () => {
  const { t } = useTranslation();
  const { closeModal, openModal, closeAllModals } = useModalStackStore();
  const queryClient = useQueryClient();
  const { data: profileData } = useGetMyProfile();

  const email = profileData?.email;

  const handleRequestEmailModification = async (email: string) => {
    await http.get(`/my/verify`, { email }, { useErrorToast: false, useLoading: false });

    openModal(
      <ConfirmBody
        transComponents={<Trans i18nKey={'setting.edit_email_verify_success_message'} />}
        title={t('setting.edit_email_verify_success_message')}
        contents={t('setting.edit_email_verify_success_info_text')}
        onClickButton={() => queryClient.invalidateQueries({ queryKey: MY_PROFILE_KEY })}
        closeAllModals={closeAllModals}
      />,
      {
        modalWrapperStyle: { maxWidth: 320 },
        closeOnBackdropClick: true,
      }
    );
  };

  const handleEmailModificationError = (error: unknown, formik?: FormikProps<{ email: string | undefined }>) => {
    if (typeof error === 'object' && error !== null && 'code' in error && 'statusCode' in error) {
      if (error.statusCode === 400 && error.code === 'UNKNOWN_ERROR_HTTP') {
        handleFailToast(t('setting.edit_email_verify_validation_text'));
        return;
      }

      if (error.statusCode === 400 && error.code === 'EMAIL_ALREADY_VERIFIED') {
        formik?.setFieldError('email', t('setting.edit_email_verify_email_template_expiration_message'));
        return;
      }

      if (error.statusCode === 400 && error.code === 'CURRENT_OWN_EMAIL_ALREADY_VERIFIED' && formik) {
        formik.setFieldError('email', t('chat.profile_my_same_verified_email'));
      }

      if (error.statusCode === 500 && error.code === 'UNKNOWN_ERROR') {
        handleFailToast(t('identity.verification_failed_reason_unknown'));
        return;
      }
    }
    handleControlAuthError(error, formik);
  };

  const handleOpenEmailModificationModal = () => {
    if (!email) return;

    openModal(
      <EmailVerifyBody
        email={email}
        isVerified={true}
        onClickSubmit={handleRequestEmailModification}
        onControlError={handleEmailModificationError}
        closeModal={closeModal}
        subTitle={'setting.edit_email_re_verify_info_text'}
      />,
      {
        closeOnBackdropClick: true,
      }
    );
  };

  return (
    <ModificationButton loadingKey={LOADING_KEY.PROFILES} onClick={handleOpenEmailModificationModal}>
      {t('chat.rewards_creator_information_edit')}
    </ModificationButton>
  );
};
